import { lazy, Suspense, type FC } from 'react'
import type { RouteObject } from 'react-router'
import { Outlet } from 'react-router-dom'
import Box from '@mui/material/Box'
import { wrapWithErrorBoundary } from './wrapWithErrorBoundary'
import { FeatureFlagRoute } from '../components/FeatureFlagRoute'
import { FeatureNames } from '@/lib/featureFlags'

// Documents
const DocumentListPage = lazy(() => import('@/pages/documents/list'))
const DocumentListAltPage = lazy(() => import('@/pages/documents/list_alt'))

const ExtractionPage = lazy(() => import('@/pages/documents/extraction'))
const ClassificationPage = lazy(
  () => import('@/pages/documents/classification')
)
const DocumentEntryPage = lazy(() => import('@/pages/documents/entry'))
/** Produit agentique : la déclaration stockée, sans rien du chemin export. */
const DeclarationPage = lazy(() => import('@/pages/documents/declaration'))

const WrappedDocumentListPage = wrapWithErrorBoundary(DocumentListPage)
const WrappedDocumentListAltPage = wrapWithErrorBoundary(DocumentListAltPage)

const WrappedExtractionPage = wrapWithErrorBoundary(ExtractionPage)
const WrappedClassificationPage = wrapWithErrorBoundary(ClassificationPage)
const WrappedDocumentEntryPage = wrapWithErrorBoundary(DocumentEntryPage)
const WrappedDeclarationPage = wrapWithErrorBoundary(DeclarationPage)

const commonProps = {
  selectedStatus: [
    'mapping',
    'mapped',
    'mapping',
    'segmented',
    'extracting',
    'upload',
    'failed',
  ],
  title: 'pendingDocuments',
  displayPendingRecords: true,
  displayFileDropZone: true,
}

/**
 * Suspense dédié : en allant vers extraction (lazy), évite une frame « vide » ou
 * contrastée ; au retour liste, le chunk est souvent déjà en cache → pas le même flash.
 */
const DocumentsSuspenseOutlet: FC = () => (
  <Suspense
    fallback={
      <Box
        sx={{
          flex: '1 1 auto',
          alignSelf: 'stretch',
          minHeight: 0,
          width: '100%',
          bgcolor: 'background.default',
        }}
      />
    }
  >
    <Outlet />
  </Suspense>
)

/** Branche à monter sous le `DashboardLayoutWrapper` unique (path `/`). */
export const documentsNestedRoute: RouteObject = {
  path: 'documents',
  element: <DocumentsSuspenseOutlet />,
  children: [
    {
      path: 'list',
      handle: {
        documentTitle: { namespace: 'nav', key: 'fieldList' },
      },
      element: (
        // <WrappedDocumentListPage
        //   selectedStatus={[
        //     'mapping',
        //     'mapped',
        //     'mapping',
        //     'segmented',
        //     'extracting',
        //     'upload',
        //     'failed',
        //   ]}
        //   title={'pendingDocuments'}
        //   displayPendingRecords={true}
        //   displayFileDropZone={true}
        // />
        <FeatureFlagRoute
          flag={FeatureNames.newTrackingView}
          enabled={WrappedDocumentListAltPage}
          disabled={WrappedDocumentListPage}
          componentProps={commonProps}
        />
      ),
    },
    {
      path: 'exported',
      handle: {
        documentTitle: { namespace: 'nav', key: 'fieldExported' },
      },
      element: (
        <WrappedDocumentListPage
          selectedStatus={['exported']}
          title={'sentDocuments'}
          displayPendingRecords={false}
          displayFileDropZone={false}
        />
      ),
    },
    {
      path: ':id',
      element: <WrappedDocumentEntryPage />,
    },
    {
      path: ':id/extraction',
      handle: {
        documentTitle: { namespace: 'extraction', key: 'extraction' },
      },
      element: <WrappedExtractionPage />,
    },
    {
      path: ':id/declaration',
      handle: {
        documentTitle: { namespace: 'extraction', key: 'declaration' },
      },
      element: <WrappedDeclarationPage />,
    },
    {
      path: ':id/classification',
      handle: {
        documentTitle: { namespace: 'common', key: 'classification' },
      },
      element: <WrappedClassificationPage />,
    },
  ],
}
